// @flow
import { action, observable, extendObservable, runInAction } from "mobx";
import moment from "moment";
import axios from "axios";
import NetworkState from "./NetworkState";
import CCCCountry from "./CCCCountry";

export default class JobLibraryRequest {
  network: NetworkState;
  store: ?Object;
  id: ?number;
  title: ?string;
  description: ?string;
  status: ?string;
  statusVerbose: ?string;
  userMessage: ?string;
  attachmentFileName: ?string;
  created: ?Object;
  createdDisplay: ?string;
  modified: ?Object;
  modifiedDisplay: ?string;
  viewState: ?{ selected?: boolean, expanded?: boolean, editing?: boolean };
  totalCertifiedCountries: ?number;
  certifiedCountries: ?(CCCCountry[]);

  toggleEdit: () => void;
  destroy: () => void;
  toggleExpanded: () => void;
  getDescription: () => void;
  toggleSelected: (SyntheticMouseEvent<HTMLElement>, ?any, ?any, ?boolean) => void;

  constructor(store: Object, payloadObject: Object) {
    this.store = store;
    this.id = null;
    this.title = null;
    this.description = null;
    this.status = null;
    this.statusVerbose = null;
    this.userMessage = null;
    this.attachmentFileName = null;
    this.created = null;
    this.createdDisplay = null;
    this.modified = null;
    this.modifiedDisplay = null;
    this.viewState = null;
    this.network = new NetworkState();
    this.totalCertifiedCountries = null;
    this.certifiedCountries = null;

    if (payloadObject) {
      let certifiedCountries = [];
      if (payloadObject.certifiedCountries) {
        certifiedCountries = payloadObject.certifiedCountries.edges.map((edge) => {
          return new CCCCountry(store, edge.node);
        });
      }

      this.id = payloadObject.databaseId || null;
      this.title = payloadObject.title || null;
      this.description = payloadObject.description || null;
      this.userMessage = payloadObject.userMessage || null;
      this.attachmentFileName = payloadObject.attachmentFileName;
      this.status = payloadObject.status;
      this.statusVerbose = payloadObject.statusVerbose;
      this.created = moment(payloadObject.created);
      this.createdDisplay = `${this.created.format("MM/DD/YYYY")}`;
      this.modified = moment(payloadObject.modified);
      this.modifiedDisplay = `${this.modified.format("MM/DD/YYYY")}`;
      this.totalCertifiedCountries = payloadObject.totalCertifiedCountries;
      this.certifiedCountries = certifiedCountries;
    }

    extendObservable(this, {
      certifiedCountries: null,
      viewState: observable({
        selected: false,
        expanded: false,
        editing: false,
      }),
    });

    this.toggleEdit = action(this.toggleEdit.bind(this));
    this.toggleSelected = action(this.toggleSelected.bind(this));
    this.destroy = action(this.destroy.bind(this));
    this.toggleExpanded = action(this.toggleExpanded.bind(this));
    this.getDescription = action(this.getDescription.bind(this));
  }

  toggleExpanded() {
    if (!this.viewState) return;
    this.viewState.expanded = !this.viewState.expanded;
    if (this.viewState.expanded) {
      this.getDescription();
    }
  }

  toggleEdit() {
    if (!this.store) return;
    const viewState = this.store.jobLibraryRequestsViewState.get(this.id);
    viewState.editing = !viewState.editing;
  }

  toggleSelected(
    e: SyntheticMouseEvent<HTMLElement>,
    ignore1?: ?any = null,
    ignore2?: ?any = null,
    setValue?: ?boolean = null
  ) {
    const store = this.store;
    if (!store) return;

    const viewState = store.jobLibraryRequestsViewState.get(this.id);

    if (setValue !== null) {
      viewState.selected = setValue;
      return;
    } else {
      viewState.selected = !viewState.selected;

      if (!viewState.selected && store.allSelected) {
        store.allSelected = false;
      }
    }

    if (!store.allowMultipleItemSelection) {
      // deselect all other rate cards
      store.jobLibraryRequestsViewState.forEach((viewState) => {
        if (this.viewState === viewState) return;

        viewState.selected = false;
      });
    }
  }

  destroy() {
    if (!this.store) return;
    this.store.jobLibraryRequests.remove(this);
  }

  toJS() {
    return {
      id: this.id,
      name: this.title,
    };
  }

  async getDescription() {
    const store = this.store;
    if (!store) return;

    if (this.network.loading) return;

    if (this.description) return;

    const query = `
    query getDescription($databaseId: ID!) {
      clientLibraryRequest(databaseId: $databaseId) {
        description
        certifiedCountries {
          edges {
            node {
              databaseId
              title 
              iso3166Alpha2
            }
          }
        }
      }
    }
    `;
    const variables = {
      databaseId: this.id,
    };

    this.network.loading = true;

    let payload = null;
    try {
      payload = await store.fetchTasteGraphQL(query, variables);
    } catch (e) {
      if (axios.isCancel(e)) {
        // console.log('Request Canceled ClientLibraryRequestListStore');
        return e;
      }

      this.network.handleError("Getting Client Library Request Description", e);
      if (payload !== null) {
        this.network.logGraphQLError(
          "Get Client Library Request Description query",
          payload
        );
      }

      // TODO: Display user friendly error message
      return e;
    }

    runInAction("getDescription--success", () => {
      this.network.loading = false;
      this.network.error = null;
      if (
        this.network.logGraphQLError(
          "Get Client Library Request Description query",
          payload
        )
      ) {
        // TODO: Display user friendly error message
        return { totalCount: 0, startCursor: "", endCursor: "" };
      }

      if (!payload) return;

      this.description = payload.data.clientLibraryRequest.description;
      if (this.store && payload.data.clientLibraryRequest.certifiedCountries) {
        const store = this.store;
        this.certifiedCountries =
          payload.data.clientLibraryRequest.certifiedCountries.edges.map((edge) => {
            return new CCCCountry(store, edge.node);
          });
      }
    });
  }

  static fromJS(store: Object, object: Object) {
    return new JobLibraryRequest(store, object);
  }
}
